import { GrowthBook } from "@growthbook/growthbook-react";
import { LocalStorageStickyBucketService } from "@growthbook/growthbook";
import mixpanel from "mixpanel-browser";
import { clientSource } from "@/lib/client-source";
import { createUuid } from "@/lib/uuid";

/**
 * GrowthBook — feature flag / deney atama katmanı.
 *
 * Sonuçlar GrowthBook'ta değil Mixpanel'de okunuyor: Mixpanel'in Experiments
 * raporu yalnızca Enterprise planında ve ayrı ücretli bir eklenti. Onun yerine
 * varyant her event'e super property olarak yazılıyor ve dönüşüm sıradan bir
 * Funnel kırılımıyla ölçülüyor. Bkz. `trackingCallback`.
 *
 * Kritik kural: **flag okuma her zaman bir fallback ile yapılır ve o fallback
 * bugünkü davranıştır.** GrowthBook erişilemezse (reklam engelleyiciler
 * `cdn.growthbook.io`'yu bloklayabiliyor) ürün bugünkü gibi çalışmaya devam
 * eder. Okuma için `useFlag` kullan — `useFeatureIsOn` kullanma, gerekçesi
 * `use-flag.ts` içinde.
 */

const DEVICE_ID_KEY = "harmony:v1:device-id";

/**
 * Giriş yapmamış kullanıcılar için kalıcı, anonim kimlik.
 *
 * İki işi var: (1) login öncesi de yüzdesel dağıtım yapılabilsin, (2) kullanıcı
 * anonimken girdiği deneyde login sonrası da aynı varyantta kalsın — sticky
 * bucketing bunu bu id üzerinden çözüyor.
 */
export function getOrCreateDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;

    const id = createUuid();
    localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch {
    // Private mode / storage kapalı: oturumluk id ile devam et. Sticky
    // bucketing çalışmaz ama flag okuma çalışır.
    return createUuid();
  }
}

/** GrowthBook feature key → Mixpanel super property adı. */
const expProp = (featureKey: string) =>
  `exp_${featureKey.replace(/-/g, "_")}`;

/**
 * Bu oturumda hangi deneye hangi varyantla girildi.
 *
 * `mixpanel.reset()` (logout) cookie'yi ve içindeki super property'leri siler;
 * `reRegisterExposures()` bunları geri yazmak için bu haritayı kullanır.
 */
const exposures = new Map<string, string>();

export const gb = new GrowthBook({
  apiHost: import.meta.env.VITE_GROWTHBOOK_API_HOST,
  clientKey: import.meta.env.VITE_GROWTHBOOK_CLIENT_KEY,
  enableDevMode: import.meta.env.VITE_APP_ENV === "dev",
  stickyBucketService: new LocalStorageStickyBucketService(),
  trackingCallback: (experiment, result) => {
    const variant = String(result.key);
    exposures.set(experiment.key, variant);

    try {
      // Asıl iş burada: `register` varyantı bundan sonraki HER event'e
      // ekliyor. Mixpanel'de funnel'ı varyanta göre kırabilmemizin tek
      // sebebi bu — event'in kendisi sadece deneye girişi işaretliyor.
      mixpanel.register({ [expProp(experiment.key)]: variant });
      // `sdk`: hangi GrowthBook SDK'sının flag'i değerlendirdiği. `apps/api`
      // aynı event'e `sdk: "api"` yazıyor. `source` ise istemci kanalı —
      // artık her event'te aynı anlama gelen tek bir property.
      mixpanel.track("experiment_viewed", {
        experiment: experiment.key,
        variant,
        sdk: "browser",
        source: clientSource(),
      });
    } catch (e) {
      console.warn("Mixpanel error:", e);
    }
  },
});

/**
 * `mixpanel.reset()` sonrası super property'leri geri yaz.
 *
 * Logout `reset()` çağırıyor (Profile.tsx) ve cookie ile birlikte varyant
 * bilgisi de gidiyor. Geri yazılmazsa çıkıp tekrar giren kullanıcı funnel
 * kırılımından sessizce düşer — hiçbir yerde hata görünmez, sadece sayılar
 * eksik olur.
 */
export function reRegisterExposures(): void {
  if (exposures.size === 0) return;

  try {
    const props: Record<string, string> = {};
    exposures.forEach((variant, key) => {
      props[expProp(key)] = variant;
    });
    mixpanel.register(props);
  } catch (e) {
    console.warn("Mixpanel error:", e);
  }
}

/**
 * Payload'ı yükler. `main.tsx` render'dan ÖNCE bunu bekler.
 *
 * Beklemek bilinçli: flag'ler route guard kararı veriyor, ilk boyamadan sonra
 * değişirlerse kullanıcı açılmış bir sayfadan atılır. Payload localStorage'da
 * cache'lendiği için dönen kullanıcıda bu anında çözülür; ilk ziyaretin maliyeti
 * timeout ile sınırlı, sonrasında fallback'lerle normal şekilde açılır.
 */
export async function initGrowthBook(): Promise<void> {
  if (!import.meta.env.VITE_GROWTHBOOK_CLIENT_KEY) {
    // Key yoksa hiç ağa çıkma; tüm flag'ler fallback döner.
    return;
  }

  try {
    // `init` başarısızlıkta THROW ETMEZ, `success: false` döner — sadece
    // try/catch'e güvenmek, bloklanmış bir CDN'i tamamen görünmez yapar.
    // Reklam engelleyiciler `cdn.growthbook.io`'yu bloklayabildiği için bu
    // teorik değil: kullanıcıların bir kısmı sürekli fallback'lerle geziyor
    // olabilir ve hiçbir yerde iz kalmaz.
    const res = await gb.init({ timeout: 1500, streaming: true });

    if (!res?.success) {
      console.warn(
        `GrowthBook payload alınamadı (source=${res?.source}), fallback'lerle devam ediliyor`,
        res?.error
      );

      // Mixpanel'e de yaz: bu olay sayılabilir olmalı. Reklam engelleyicilerin
      // ne kadar kullanıcıyı sürekli fallback'te bıraktığını bilmeden,
      // "CDN'i kendi alan adımızın arkasına alalım mı" sorusu cevaplanamaz.
      // Sentry yerine Mixpanel: bu bir hata değil, oran olarak anlamlı bir sayı.
      try {
        // `payload_source`, `source` değil: buradaki değer GrowthBook
        // payload'ının nereden geldiği ("network" / "cache"), istemci kanalı
        // değil. `source` artık her event'te tek bir şey ifade ediyor.
        mixpanel.track("growthbook_unavailable", {
          payload_source: res?.source,
        });
      } catch {
        /* analytics asla akışı bozmaz */
      }
    }
  } catch (e) {
    console.warn("GrowthBook init failed, using fallbacks:", e);
  }
}
